/**
 * Financial Setup Page — /finance/setup
 *
 * Consolidated setup page with tabs for all finance master data:
 *   1. Legal Entities
 *   2. Bank Accounts
 *   3. Chart of Accounts
 *   4. Charge Codes
 *   5. Tax Config
 *   6. Exchange Rates
 *   7. Accounting Periods
 *   8. Fixed Assets
 *
 * Replaces separate sidebar setup items with 1 tabbed setup surface.
 * Each tab lazy-renders its existing page component.
 */

import { lazy, Suspense, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Settings2, Loader2, Network, Calendar, Percent, Tag, RefreshCw, Building2, Landmark } from 'lucide-react';

const ChartOfAccountsPage = lazy(() => import('./ChartOfAccountsPage'));
const AccountingPeriodsPage = lazy(() => import('./AccountingPeriodsPage'));
const TaxConfigPage = lazy(() => import('./TaxConfigPage'));
const ChargeCodesPage = lazy(() => import('./ChargeCodesPage'));
const ExchangeRatesPage = lazy(() => import('./ExchangeRatesPage'));
const LegalEntitiesPage = lazy(() => import('./LegalEntitiesPage'));
const BankAccountsPage = lazy(() => import('./BankAccountsPage'));
const FixedAssetsPage = lazy(() => import('./FixedAssetsPage'));

const TAB_DEFS = [
  { value: 'entities', label: 'Legal Entities',     icon: Building2 },
  { value: 'bank',     label: 'Bank Accounts',      icon: Landmark },
  { value: 'coa',      label: 'Chart of Accounts',  icon: Network },
  { value: 'charges',  label: 'Charge Codes',       icon: Tag },
  { value: 'tax',      label: 'Tax Config',         icon: Percent },
  { value: 'fx',       label: 'Exchange Rates',     icon: RefreshCw },
  { value: 'periods',  label: 'Accounting Periods', icon: Calendar },
  { value: 'assets',   label: 'Fixed Assets',       icon: Building2 },
] as const;

type TabKey = (typeof TAB_DEFS)[number]['value'];

const TAB_FALLBACK = (
  <div className="flex min-h-[300px] items-center justify-center">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

export default function FinancialSetupPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TabKey) || 'entities';

  const handleTabChange = useCallback((value: string) => {
    setSearchParams({ tab: value }, { replace: true });
  }, [setSearchParams]);

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Settings2 className="h-6 w-6 text-emerald-600" />
        <div>
          <h1 className="text-xl font-semibold">Financial Setup</h1>
          <p className="text-sm text-muted-foreground">
            Legal entities, bank accounts, chart of accounts, tax, charge codes, exchange rates, periods, and fixed assets
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="w-full flex overflow-x-auto">
          {TAB_DEFS.map(({ value, label, icon: Icon }) => (
            <TabsTrigger key={value} value={value} className="flex items-center gap-1.5 whitespace-nowrap text-xs sm:text-sm">
              <Icon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{label}</span>
              <span className="sm:hidden">{label.split(' ')[0]}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="entities" className="mt-4">
          <Suspense fallback={TAB_FALLBACK}><LegalEntitiesPage /></Suspense>
        </TabsContent>
        <TabsContent value="bank" className="mt-4">
          <Suspense fallback={TAB_FALLBACK}><BankAccountsPage /></Suspense>
        </TabsContent>
        <TabsContent value="coa" className="mt-4">
          <Suspense fallback={TAB_FALLBACK}><ChartOfAccountsPage /></Suspense>
        </TabsContent>
        <TabsContent value="charges" className="mt-4">
          <Suspense fallback={TAB_FALLBACK}><ChargeCodesPage /></Suspense>
        </TabsContent>
        <TabsContent value="tax" className="mt-4">
          <Suspense fallback={TAB_FALLBACK}><TaxConfigPage /></Suspense>
        </TabsContent>
        <TabsContent value="fx" className="mt-4">
          <Suspense fallback={TAB_FALLBACK}><ExchangeRatesPage /></Suspense>
        </TabsContent>
        <TabsContent value="periods" className="mt-4">
          <Suspense fallback={TAB_FALLBACK}><AccountingPeriodsPage /></Suspense>
        </TabsContent>
        <TabsContent value="assets" className="mt-4">
          <Suspense fallback={TAB_FALLBACK}><FixedAssetsPage /></Suspense>
        </TabsContent>
      </Tabs>
    </div>
  );
}
